import axios from 'axios'
import i18next from 'i18next'
import moment from 'moment'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Sidebar } from 'primereact/sidebar'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { bindActionCreators } from 'redux'

// local imports (assets)
import failedImage from '../../assets/images/failed-image.svg'
import notificationSuccess from '../../assets/images/notification-success.svg'
import successDialogImage from '../../assets/images/success-image.svg'
import './CalendarHomePage.scss'

/* Types - Services - Utils */
import HealthService from 'service/HealthService'
import CalendarService from '../../service/CalendarService'
import EmployeeService from '../../service/EmployeeService'
import HomeOfficeService from '../../service/HomeOfficeService'
import ReservationService from '../../service/ReservationService'
import { actionCreators } from '../../state/state-index'
import { PlanTypes, TransactionResponseTypes } from '../../types/BaseType'
import { ICalendarPlan, ISideBarModel, ViewSideBar, VisiblePlan } from '../../types/CalendarType'
import { ICalendarPersonnalModel, ICalendarSquads, IEmployeeSummary, ISearchFirmEmployee } from '../../types/Employee'
import { ReservationMeta, RoutingType } from '../../types/FloorType'
import { IRemoteOfficeType } from '../../types/MainType'
import { PlanTabs } from '../../types/PlanTabs'
import AuthUtil from '../../utils/AuthUtil'
import useDidMountEffect from '../../utils/useDidMountEffect'

/* Components */
import NotificationDialog from '../ui/NotificationDialog'
import CalendarCoiffeurSearchModal from './calendar-coiffeur-modal/CalendarCoiffeurSelectModal'
import DoctorSelectModal from './calendar-doctor-modal/DoctorSelectModal'
import CalendarGymSearchModal from './calendar-gym-modal/CalendarGymSearchModal'
import LactationRoomSearchModal from './calendar-lactation-modal/LactationRoomSelectModal'
import MeetingRoomSearchModal from './calendar-meeting-modal/MeetingRoomSelectModal'
import CalendarPersonnelList from './calendar-personnel-list/CalendarPersonnelList'
import PoolVehicleSearchModal from './calendar-pool-vehicle-modal/PoolVehicleSelectModal'
import CampusPlanSideBar from './calendar-sidebar/CampusPlanSidebar'
import MultiplePlanSideBar from './calendar-sidebar/multi-plan/MultiplePlanSideBar'
import NewPlanSideBar from './calendar-sidebar/new-plan/NewPlanSideBar'
import OfficePlanSideBar from './calendar-sidebar/office-plan/OfficePlanSideBar'
import RemotePlanSideBar from './calendar-sidebar/remote-plan/RemotePlanSideBar'
import SquadsEventsSideBar from './calendar-sidebar/squads-plan/SquadsEventsSideBar'
import CalendarStatistic from './CalendarStatistic'
import OfCalendar from './of-calendar/OfCalendar'

const CalendarHomePage = () => {
  const { t } = useTranslation()
  const [calendarEvents, setCalendarEvents] = useState([] as ICalendarPlan[])
  const [calendarMonth, setCalendarMonth] = useState(moment())
  const [visibleRight, setVisibleRight] = useState(false)
  const [employeeSummary, setEmployeeSummary] = useState({} as IEmployeeSummary)
  const [calendarPersonnalModel, setCalendarPersonnalModel] = useState({} as ICalendarPersonnalModel)
  const [selectedPersonnal, setSelectedPersonnal] = useState({} as IEmployeeSummary)
  const [sideBarModel, setSideBarModel] = useState({} as ISideBarModel)
  const [displayAlertDialog, setDisplayAlertDialog] = useState(false)
  const [displayDeleteAlertDialog, setDisplayDeleteAlertDialog] = useState(false)
  const [displayDeleteOfficeAlertDialog, setDisplayDeleteOfficeAlertDialog] = useState(false)
  const [reservationMeta] = useState<ReservationMeta | undefined>()
  const [showCalendarLoader, setShowCalendarLoader] = useState(false)
  const [successDeletionText, setSuccessDeletionText] = useState('')
  const [reservationFailed, setReservationFailed] = useState<boolean>(false)
  const [reservationFailedText, setReservationFailedText] = useState<string>()
  const [searchFirmEmployee, setSearchFirmEmployee] = useState<ISearchFirmEmployee[]>()
  const history = useHistory()
  const [showSquadsEvent, setShowSquadsEvent] = useState<boolean>(false)
  const [isMyCalendar, setIsMyCalendar] = useState<boolean>(true)
  const [squadsEvents, setSquadsEvents] = useState<ICalendarSquads[] | undefined>(undefined)
  const [squadsEventsData, setSquadsEventsData] = useState<ICalendarSquads[] | undefined>(undefined)

  const [visiblityMultipleSideBar, setVisibilityMultipleSideBar] = useState(false)
  const [sideBarModelList, setSideBarModelList] = useState([] as ISideBarModel[])
  const [types, setTypes] = useState<IRemoteOfficeType[]>([])
  const [selectedType, setSelectedType] = useState<IRemoteOfficeType | null>(null)
  const daysState = useSelector((state: any) => state.calendars)
  const dispatch = useDispatch()
  const { cleanDaysState } = bindActionCreators(actionCreators, dispatch)
  const [ofDay, setOfDay] = useState<any[] | undefined>([])
  const [ofDayListCount, setOfDayListCount] = useState<number>(0)

  let personnalListRef = useRef<any>()
  let statisticRef = useRef<any>()

  let deletionTexts = [t('deletion-texts.firm-office-deletion'), t('deletion-texts.shared-office-deletion')]

  useEffect(() => {
    cleanDaysState(true)
    fetchCalendarUIData()
    getCurrentEmployeeSummary()
    getMyPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18next.language])

  useEffect(() => {
    HomeOfficeService.getRemoteOfficeTypeList().then((resp: any) => {
      let officeTypes = resp.data || []
      const firstType = officeTypes?.find((t: IRemoteOfficeType) => t.id === 0)
      officeTypes = officeTypes?.filter((item: IRemoteOfficeType) => item.id !== 0)
      officeTypes?.unshift(firstType)
      setTypes(officeTypes)
      if (resp.data.length > 0) setSelectedType(officeTypes[0])
    })
  }, [])

  useEffect(() => {
    if (reservationMeta) {
      history.push({ pathname: `/reservation`, state: { response: reservationMeta } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationMeta])

  useEffect(() => {
    fetchCalendarUIData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth])

  useEffect(() => {
    fetchCalendarUIData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPersonnal])

  useEffect(() => {
    let { id, isManager } = employeeSummary
    if (isManager === null || isManager === undefined) {
    } else {
      if (isManager === true) getCalendarPersonnalModel(id)
      else getMyTeams(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSummary])

  useEffect(() => {
    if (successDeletionText && successDeletionText.length > 0) setDisplayDeleteOfficeAlertDialog(true)
  }, [successDeletionText])

  let getStartAndEndDate = () => {
    // get -1 - +1 month information
    let previousMonthFirstDay = calendarMonth?.clone().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
    let nextMonthLastDay = calendarMonth?.clone().add(1, 'months').endOf('month').format('YYYY-MM-DD')
    return {
      startOfMonth: previousMonthFirstDay,
      endOfMonth: nextMonthLastDay,
    }
  }

  const getMyPlans = () => {
    let offDayList: any[] = []
    setShowCalendarLoader(true)
    let { startOfMonth, endOfMonth } = getStartAndEndDate()
    CalendarService.getMyPlans(startOfMonth, endOfMonth)
      .then((response) => {
        response.data?.map((item) => {
          if (item.planType === 7) offDayList.push(item)
          return item
        })
        setOfDay(offDayList)
        setOfDayListCount(offDayList.length)
        setCalendarEvents(response.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setShowCalendarLoader(false))
  }

  const onChangeCalendarMonth = (arg: any) => {
    setCalendarMonth(arg.clone())
  }

  const onSearchFirmEmployee = (arg: string) => {
    getSearchFirmEmployeeByFullName(arg)
  }

  const getSearchFirmEmployeeByFullName = (searchText: string) => {
    EmployeeService.getSearchFirmEmployeeByFullName(searchText).then((response) => {
      // eslint-disable-next-line array-callback-return
      response.data.map((firmEmployee) => {
        firmEmployee._photoUrl = EmployeeService.getEmployeePhotoUrl(firmEmployee)
      })
      setSearchFirmEmployee(response.data)
    })
  }

  const isDateInRange = (day: any) => {
    // Servisten Dönen Gün Sayısına Göre Rezervasyon Oluşturma ve
    // Var olan Rezervasyonun Sil ve Görünlüme Fonk. Kısıtlanır
    let emp = AuthUtil.getEmployeeSummary()
    let forwardDaysReach = emp != null ? emp.futureOrientedDayCount : 0
    let backwardDaysReach = emp != null ? emp.pastOrientedDayCount : 0
    let firstDay = moment().subtract(backwardDaysReach, 'days').startOf('days')
    //let previousMonthFirstDay = moment().subtract(1, 'months').startOf('month');
    let lastDay = moment().add(forwardDaysReach, 'days').startOf('day')
    let isDateInRange = false
    if (day) {
      if (day >= firstDay && day <= lastDay) {
        isDateInRange = true
      }
    }
    return isDateInRange
  }

  const fetchCalendarUIData = () => {
    let offDayList: any[] = []
    let { id } = selectedPersonnal
    if (id) {
      setShowCalendarLoader(true)
      let { startOfMonth, endOfMonth } = getStartAndEndDate()
      CalendarService.getPlanList(id, startOfMonth, endOfMonth)
        .then((response) => {
          // eslint-disable-next-line array-callback-return
          response.data?.map((item) => {
            if (item.planType === 7) {
              offDayList.push(item)
            }
          })
          setOfDay(offDayList)
          setOfDayListCount(offDayList.length)
          setCalendarEvents(response.data)
        })
        .catch((e) => console.log(e))
        .finally(() => setShowCalendarLoader(false))
    }
  }

  const getCreatePlanRequest = (val: any) => {
    let { day } = val
    let { date } = day
    let dateFormat = date.format('YYYY-MM-DD')
    let remoteOfficeTypeId = selectedType?.id
    if (dateFormat) {
      return types.length > 0
        ? {
            reservationDate: dateFormat,
            reservationEndDate: dateFormat,
            remoteOfficeTypeId: remoteOfficeTypeId,
          }
        : {
            ReservationEndDate: dateFormat,
            RemoteOfficeTypeId: remoteOfficeTypeId,
          }
    } else {
      return null
    }
  }

  const onClickCreateHomePlan = (val: any) => {
    let request = getCreatePlanRequest(val)
    if (request) {
      HomeOfficeService.createHomePlan(request)
        .then((response) => {
          let { data } = response
          if (data.transactionResponseType === TransactionResponseTypes.Fail) {
            setReservationFailed(true)
            // Backend'den gelen mesaj kodunu dictionary'den çevir
            const errorCode = data.message
            const translatedMessage = t(errorCode, { ns: 'dictionary' })
            setReservationFailedText(translatedMessage)
          } else {
            setVisibleRight(false)
            fetchCalendarUIData()
            statisticRef?.current?.reloadStatisticData()
            // Sidebar kapanma animasyonu bittikten sonra başarı popup'ını aç
            setTimeout(() => setDisplayAlertDialog(true), 350)
          }
        })
        .catch((e) => {
          console.log(e)
          // BusinessException mesajı gösterme
          // Interceptor hata popup'ı gösterecek
        })
    }
  }

  const onClickDeleteHomePlan = (val: any) => {
    let { event } = val
    let { planType } = event
    if (planType === PlanTypes.HomeOffice) {
      let request = {
        id: event?.id,
      }
      if (request) {
        HomeOfficeService.deleteHomePlan(request)
          .then((response) => {
            let { data } = response
            if (data.transactionResponseType === TransactionResponseTypes.Fail) {
              setDisplayDeleteAlertDialog(false)
            } else {
              setDisplayDeleteAlertDialog(true)
              fetchCalendarUIData()
              statisticRef?.current?.reloadStatisticData()
              setVisibleRight(false)
            }
          })
          .catch((e) => {
            setDisplayDeleteAlertDialog(false)
            console.log(e)
          })
      }
    }
  }

  const redirectPage = (_planTab: PlanTabs, _obj: any) => {
    let { day } = _obj
    let { date } = day
    let reservationMeta = {
      isNew: true,
      startDate: date.toDate(),
      endDate: date.toDate(),
      planTab: _planTab,
      selectedFloor: null,
      routingType: RoutingType.CALENDAR_PAGE,
    }

    history.replace({
      pathname: `/reservation`,
      state: {
        response: reservationMeta,
      },
    })
  }

  const redirectPageForMultipleCommonPlan = (_planTab: PlanTabs) => {
    let dateList: any[] = []
    sideBarModelList.forEach((element) => {
      if (element.day.isChecked) {
        dateList.push(new Date(element.day.date._d))
      }
    })
    dateList.sort((date1, date2) => date1 - date2)

    let reservationMeta = {
      isNew: true,
      planTab: _planTab,
      startDate: dateList[0],
      endDate: dateList[dateList.length - 1],
      selectedFloor: null,
      routingType: RoutingType.CALENDAR_PAGE,
    }

    history.replace({
      pathname: `/reservation`,
      state: {
        response: reservationMeta,
      },
    })
  }

  const onClickCreateOfficePlan = (val: any) => {
    redirectPage(PlanTabs.OFFICE, val)
  }

  const onClickCreateMultipleOfficePlan = (val: any) => {
    redirectPageForMultipleCommonPlan(PlanTabs.OFFICE)
  }

  const onClickCreateMultipleCommonPlan = () => {
    redirectPageForMultipleCommonPlan(PlanTabs.COMMON)
  }

  const onClickDeleteOfficePlan = (val: any) => {
    let { event } = val
    let request = { id: event?.id }
    if (!(event.planType === 5 || event.planType === 6)) {
      ReservationService.deleteReservation(request)
        .then((response) => {
          let { data } = response
          if (data.transactionResponseType === TransactionResponseTypes.Fail) {
            setDisplayDeleteOfficeAlertDialog(false)
          } else {
            let { planType } = event
            let ind = 0
            if (planType === PlanTypes.OtherLocationTableReservation) ind = 1
            setSuccessDeletionText(deletionTexts[ind])
            fetchCalendarUIData()
            statisticRef?.current?.reloadStatisticData()
            setVisibleRight(false)
          }
        })
        .catch((e) => {
          setDisplayDeleteOfficeAlertDialog(false)
          console.log(e)
        })
    } else {
      ReservationService.deleteFloorReservation(request)
        .then((response) => {
          let { data } = response
          if (data.transactionResponseType === TransactionResponseTypes.Fail) {
            setDisplayDeleteOfficeAlertDialog(false)
          } else {
            let { planType } = event
            let ind = 0
            if (
              planType === PlanTypes.OtherLocationTableReservation ||
              planType === PlanTypes.OtherLocationTableFloorReservation
            )
              ind = 1
            setSuccessDeletionText(deletionTexts[ind])
            fetchCalendarUIData()
            statisticRef?.current?.reloadStatisticData()
            setVisibleRight(false)
          }
        })
        .catch((e) => {
          setDisplayDeleteOfficeAlertDialog(false)
          console.log(e)
        })
    }
  }

  const onClickCreateCommonPlan = (val: any) => {
    redirectPage(PlanTabs.COMMON, val)
  }
  const onClickRedirectOfficePlan = (val: any) => {
    let { event } = val
    let { id, planType } = event
    ReservationService.getReservation(id)
      .then((response) => {
        let { data } = response
        let {
          id,
          floorId,
          blockId,
          locationId,
          districtId,
          cityId,
          countryId,
          locationFirmId,
          blockName,
          locationName,
          districtName,
          cityName,
          floorName,
          countryName,
          locationFirmName,
          locationMapsUrl,
          locationRedirectMapsUrl,
          reservationDate,
        } = data

        let _selectedFloor = {
          id: id,
          _id: floorId,
          _fullLocation: locationName + ', ' + districtName + ', ' + blockName + ', ' + floorName,
          blockId: blockId,
          locationId: locationId,
          districtId: districtId,
          cityId: cityId,
          countryId: countryId,
          locationFirmId: locationFirmId,
          floorTemplateId: null,
          floorTemplateName: null,
          floorName: floorName,
          blockName: blockName,
          locationName: locationName,
          districtName: districtName,
          cityName: cityName,
          countryName: countryName,
          locationFirmName: locationFirmName,
          locationMapsUrl: locationMapsUrl,
          locationRedirectMapsUrl: locationRedirectMapsUrl,
          occupancy: null,
        }

        let reservationMeta = {
          isNew: false,
          startDate: new Date(reservationDate),
          endDate: new Date(reservationDate),
          planTab: PlanTabs.OFFICE,
          selectedFloor: _selectedFloor,
        }

        if (planType === PlanTypes.OtherLocationTableReservation) reservationMeta.planTab = PlanTabs.COMMON
        if (planType === PlanTypes.TableReservation) reservationMeta.planTab = PlanTabs.OFFICE

        history.replace({
          pathname: '/reservation',
          state: {
            response: reservationMeta,
          },
        })
      })
      .catch((e) => {
        console.log(e)
      })
  }

  /* TODO: add campus services to common remote and office work sidebars!! */
  const handleCalendarSelectedDayClick = (val: any) => {
    let { event, day } = val
    // check the date is rule day range
    let { date } = day
    let isDayInRange = isDateInRange(date)
    // check current user or not
    let isCurrentUser = employeeSummary.id === selectedPersonnal.id

    if (event === null || event === undefined) {
      // NEW PLAN
      if (!isDayInRange || !isCurrentUser) return

      let plan = { ...val, planType: VisiblePlan.NEW_PLAN, view: ViewSideBar.CREATE, user: selectedPersonnal }
      setSideBarModel(plan)
    } else if (event.length > 0) {
      let plan = {} as ISideBarModel
      plan = {
        ...val,
        planType: VisiblePlan.SQUADS_PLAN,
      }
      setSideBarModel(plan)
    } else {
      let { planType } = event
      if (planType === PlanTypes.HomeOffice) {
        // Remote Plan
        let plan = {} as ISideBarModel
        plan = {
          ...val,
          selectedType: types.length > 0 ? selectedType : null,
          planType: VisiblePlan.HOME_PLAN,
          view: ViewSideBar.VIEW,
          user: selectedPersonnal,
        }
        if (isDayInRange) {
          plan.view = isCurrentUser ? ViewSideBar.CANCEL : ViewSideBar.VIEW
        } else {
          plan.view = ViewSideBar.VIEW
        }
        setSideBarModel(plan)
      } else if (planType === PlanTypes.TableReservation) {
        // Office Plan
        let plan = {} as ISideBarModel
        plan = { ...val, planType: VisiblePlan.OFFICE_PLAN, view: ViewSideBar.VIEW, user: selectedPersonnal }
        if (isDayInRange) {
          plan.view = isCurrentUser ? ViewSideBar.EDIT : ViewSideBar.CANCEL
        } else {
          plan.view = ViewSideBar.VIEW
        }
        setSideBarModel(plan)
      } else if (planType === PlanTypes.TableFloorReservation) {
        // Office Plan
        let plan = {} as ISideBarModel
        plan = { ...val, planType: VisiblePlan.OFFICE_PLAN, view: ViewSideBar.VIEW, user: selectedPersonnal }
        if (isDayInRange) {
          plan.view = isCurrentUser ? ViewSideBar.EDIT : ViewSideBar.CANCEL
        } else {
          plan.view = ViewSideBar.VIEW
        }
        setSideBarModel(plan)
      } else if (planType === PlanTypes.OtherLocationTableReservation) {
        // Common Plan

        let plan = { ...val, planType: VisiblePlan.COMMON_PLAN, view: ViewSideBar.VIEW, user: selectedPersonnal }
        if (isDayInRange) {
          plan.view = isCurrentUser ? ViewSideBar.EDIT : ViewSideBar.CANCEL
        } else {
          plan.view = ViewSideBar.VIEW
        }
        setSideBarModel(plan)
      } else if (planType === PlanTypes.OtherLocationTableFloorReservation) {
        // Common Plan

        let plan = { ...val, planType: VisiblePlan.COMMON_PLAN, view: ViewSideBar.VIEW, user: selectedPersonnal }
        if (isDayInRange) {
          plan.view = isCurrentUser ? ViewSideBar.EDIT : ViewSideBar.CANCEL
        } else {
          plan.view = ViewSideBar.VIEW
        }
        setSideBarModel(plan)
      } else {
        // Campus
        let plan = { ...val, view: ViewSideBar.VIEW, user: selectedPersonnal }
        setSideBarModel(plan)
      }
    }

    setVisibleRight(true)
  }

  const handleMultipleCalendarSelectedDay = () => {
    let tempSideBarModelList: any = []

    if (daysState !== undefined && daysState.length > 0) {
      daysState.forEach((item: any) => {
        let { date } = item.day
        let planType

        if (Object.keys(item.event).length > 0 && item.event !== undefined) {
          if (item.event.planType === PlanTypes.HomeOffice || item.event.planType === 7) {
            planType = VisiblePlan.HOME_PLAN
          } else if (
            item.event.planType === PlanTypes.TableReservation ||
            item.event.planType === PlanTypes.TableFloorReservation
          ) {
            planType = VisiblePlan.OFFICE_PLAN
          } else if (
            item.event.planType === PlanTypes.OtherLocationTableReservation ||
            item.event.planType === PlanTypes.OtherLocationTableFloorReservation
          ) {
            planType = VisiblePlan.COMMON_PLAN
          } else {
            planType = VisiblePlan.NEW_PLAN
          }
        } else {
          planType = VisiblePlan.NEW_PLAN
        }

        let isDayInRange = isDateInRange(date)
        // check current user or not
        let isCurrentUser = employeeSummary.id === selectedPersonnal.id

        if (!isDayInRange || !isCurrentUser) return

        let plan = {
          ...item,
          planType: planType,

          view: ViewSideBar.MULTIPLE,
          user: selectedPersonnal,
        }
        tempSideBarModelList.push(plan)
      })
    }

    setSideBarModelList([...tempSideBarModelList])
    setVisibilityMultipleSideBar(true)
  }

  const getPlanUI = () => {
    let { planType } = sideBarModel
    if (planType === VisiblePlan.NEW_PLAN) {
      return (
        <NewPlanSideBar
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          types={types}
          model={sideBarModel}
          onClickOfficePlan={onClickCreateOfficePlan}
          onClickCommonPlan={onClickCreateCommonPlan}
          onClickRemotePlan={onClickCreateHomePlan}
        />
      )
    } else if (planType === VisiblePlan.HOME_PLAN) {
      return <RemotePlanSideBar model={sideBarModel} onClickDeleteRemotePlan={onClickDeleteHomePlan} />
    } else if (planType === VisiblePlan.OFFICE_PLAN) {
      return (
        <OfficePlanSideBar
          model={sideBarModel}
          onClickDeleteOfficePlan={onClickDeleteOfficePlan}
          onClickRedirectOfficePlan={onClickRedirectOfficePlan}
        />
      )
    } else if (planType === VisiblePlan.COMMON_PLAN) {
      return (
        <OfficePlanSideBar
          model={sideBarModel}
          onClickDeleteOfficePlan={onClickDeleteOfficePlan}
          onClickRedirectOfficePlan={onClickRedirectOfficePlan}
        />
      )
    } else if (planType === VisiblePlan.SQUADS_PLAN) {
      return <SquadsEventsSideBar model={sideBarModel} />
    } else {
      return <CampusPlanSideBar model={sideBarModel} />
    }

    // return (
    //   <NewPlanSideBar
    //     model={sideBarModel}
    //     selectedType={selectedType}
    //     setSelectedType={setSelectedType}
    //     types={types}
    //     onClickOfficePlan={onClickCreateOfficePlan}
    //     onClickCommonPlan={onClickCreateCommonPlan}
    //     onClickRemotePlan={onClickCreateHomePlan}
    //   />
    // )
  }

  const getMultiplePlanUI = () => {
    return (
      <MultiplePlanSideBar
        refreshCalendar={refreshCalendar}
        setCongratsDialog={setCongratsDialog}
        closeSidebar={() => setVisibilityMultipleSideBar(false)}
        model={sideBarModelList}
        selectedType={selectedType}
        types={types}
        setSelectedType={setSelectedType}
        onClickOfficePlan={onClickCreateMultipleOfficePlan}
        onClickHealthPlan={openDoctorModal}
        onClickCoiffeurPlan={openCoiffeurModal}
        onClickGymPlan={openGymModal}
        onClickLactationPlan={openLactationModal}
        onClickMeetingPlan={openMeetingModal}
        onClickPoolVehiclePlan={openPoolVehicleModal}
        onClickCommonPlan={onClickCreateMultipleCommonPlan}
        onClickRemotePlan={onClickCreateHomePlan}
        onClickRedirectOfficePlan={onClickRedirectOfficePlan}
      />
    )
  }

  const onClickPersonnel = (personnal: any) => {
    setShowSquadsEvent(false)
    setSelectedPersonnal({ ...personnal })
    cleanDaysState(true)
  }

  const getCurrentEmployeeSummary = () => {
    let employeeId = 0
    let currentEmployee = localStorage.getItem('employee')
    if (!(currentEmployee === null || currentEmployee === undefined)) {
      let employee = JSON.parse(currentEmployee) as IEmployeeSummary
      employeeId = employee.id
    }
    EmployeeService.getEmployeeSummery(employeeId)
      .then((response) => {
        let empSum = response.data
        empSum._photoUrl = EmployeeService.getEmployeePhotoUrl(empSum)
        setEmployeeSummary(empSum)
        setSelectedPersonnal({ ...empSum })
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const getCalendarPersonnalModel = (employeeId: number) => {
    let reqOne = EmployeeService.getTeamEmployeeSummaryList(employeeId)
    let reqTwo = EmployeeService.getEmployeeSummeryListByManagerId(employeeId)

    axios
      .all([reqOne, reqTwo])
      .then(
        axios.spread((...responses) => {
          let teams = [...responses[0].data]
          teams.forEach((s) => {
            s._photoUrl = EmployeeService.getEmployeePhotoUrl(s)
          })

          let squads = [...responses[1].data]
          squads.forEach((t) => {
            t._photoUrl = EmployeeService.getEmployeePhotoUrl(t)
          })

          let calendarPersonnal: ICalendarPersonnalModel = { teams: teams, squads: squads }
          setCalendarPersonnalModel(calendarPersonnal)
        })
      )
      .catch((e) => {
        console.log(e)
      })
  }

  const getMyTeams = (employeeId: number) => {
    EmployeeService.getTeamEmployeeSummaryList(employeeId)
      .then((response) => {
        let teams = [...response.data]
        teams.forEach((t) => {
          t._photoUrl = EmployeeService.getEmployeePhotoUrl(t)
        })
        let calendarPersonnal: ICalendarPersonnalModel = { teams: teams, squads: [] }
        setCalendarPersonnalModel(calendarPersonnal)
        console.log('calendarPersonnal', calendarPersonnal);
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const onClickSquadsEvents = (personnal: any) => {
    personnal.MySquad.forEach((t: IEmployeeSummary) => {
      t._photoUrl = EmployeeService.getEmployeePhotoUrl(t)
    })

    getCalendarSquad(personnal.MySquad)
  }

  const onSquadsEvent = (personnal: any) => {
    getCalendarSquad(calendarPersonnalModel.squads)
  }

  useDidMountEffect(() => {
    setSquadsEvents(squadsEventsData)
    setShowSquadsEvent(true)
  }, [squadsEventsData])

  const getCalendarSquad = (squads: IEmployeeSummary[]) => {
    if (squads && squads.length > 0) {
      setShowCalendarLoader(true)
      let { startOfMonth, endOfMonth } = getStartAndEndDate()
      const promises = squads.map((squad) =>
        CalendarService.getPlanList(squad.id, startOfMonth, endOfMonth).then((response) => ({
          events: response.data,
          info: squad,
          type: 'all',
        }))
      )
      Promise.all([...promises]).then(function (values) {
        let offDayList: any[] = []
        // eslint-disable-next-line array-callback-return
        values[0]?.events?.map((item) => {
          if (item.planType === 7) {
            offDayList.push(item)
          }
        })
        setOfDay(offDayList)
        setOfDayListCount(offDayList.length)
        setShowCalendarLoader(false)
        setSquadsEventsData(values)
      })
    }
  }

  const filterSquadsEvents = (squadEventType: any) => {
    let tempData = !!squadsEventsData ? (JSON.parse(JSON.stringify(squadsEventsData)) as ICalendarSquads[]) : []
    let _squadEventType = 'all'
    if (squadEventType !== _squadEventType) {
      tempData?.forEach((squad) => {
        let _event: ICalendarPlan[] = []
        squad.events.forEach((event) => {
          if (event.planType === PlanTypes.HomeOffice) _squadEventType = 'remote'
          else if (event.planType === PlanTypes.TableReservation || event.planType === PlanTypes.TableFloorReservation)
            _squadEventType = 'office'
          else if (
            event.planType === PlanTypes.OtherLocationTableReservation ||
            event.planType === PlanTypes.OtherLocationTableFloorReservation
          )
            _squadEventType = 'commonOffice'
          if (squadEventType === _squadEventType) {
            _event.push(event)
          }
        })
        squad.events = _event
        squad.type = squadEventType
      })
    } else {
      setSquadsEvents(squadsEventsData)
    }
    setSquadsEvents(tempData)
  }

  const renderFooter = () => {
    return <div className="d-flex flex-row of-confirm-footer"></div>
  }
  const renderRemoteSuccessFooter = () => {
    return (
      <div className="d-flex flex-row of-confirm-footer">
        <Button
          label={t('buttons.ok')}
          onClick={() => setDisplayAlertDialog(false)}
          className="w-100 of-button-primary"
        />
      </div>
    )
  }
  const alertDialogUI = () => {
    return (
      <Dialog
        header=""
        visible={displayAlertDialog}
        position={'center'}
        modal
        style={{ width: '400px' }}
        footer={renderRemoteSuccessFooter()}
        onHide={() => setDisplayAlertDialog(false)}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={notificationSuccess} alt="" />
          </div>
          <div className="">
            <p className="of-confirm-info">{t('dashboard.remote-office-notification')}</p>
          </div>
          <div className="of-confirm-detail">
            <p>{t('success-text.remote-office-success')}</p>
          </div>
        </div>
      </Dialog>
    )
  }
  const successAlertDialogDeleteHomePlan = () => {
    return (
      <Dialog
        header=""
        visible={displayDeleteAlertDialog}
        position={'center'}
        modal
        style={{ width: '400px' }}
        footer={renderFooter()}
        onHide={() => setDisplayDeleteAlertDialog(false)}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={successDialogImage} alt="" />
          </div>

          <div className="of-confirm-detail mt-3">
            <p>{t('deletion-texts.remote-office-deletion')}</p>
          </div>
        </div>
      </Dialog>
    )
  }
  const successAlertDialogDeleteOfficePlan = () => {
    return (
      <Dialog
        header=""
        visible={displayDeleteOfficeAlertDialog}
        position={'center'}
        modal
        style={{ width: '400px' }}
        footer={renderFooter()}
        onHide={() => setDisplayDeleteOfficeAlertDialog(false)}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={successDialogImage} alt="" />
          </div>

          <div className="of-confirm-detail mt-3">
            <p>{successDeletionText}</p>
          </div>
        </div>
      </Dialog>
    )
  }
  const errorNotification = () => {
    return (
      <NotificationDialog
        display={reservationFailed}
        imgSrc={failedImage}
        goHomeButton={true}
        types={[]}
        selectedType={null}
        setSelectedType={[]}
        title={t('failed.failed-reservation')}
        content={reservationFailedText}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={() => setReservationFailed(false)}
        onSubmit={() => {
          setReservationFailed(false)
          history.push('/calendar')
        }}
      />
    )
  }
  /* No Doctor Appointment */
  const appointmentNotFoundNotification = () => {
    return (
      <NotificationDialog
        display={appointmentNotFound}
        imgSrc={failedImage}
        goHomeButton={true}
        types={[]}
        selectedType={null}
        setSelectedType={[]}
        title={t('failed.warning-title')}
        content={t('failed.no-reservation-found')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={() => setAppointmentNotFound(false)}
        onSubmit={() => setAppointmentNotFound(false)}
      />
    )
  }

  const onIsMyCalendar = (data: any) => setIsMyCalendar(data)

  const refreshCalendar = () => {
    fetchCalendarUIData()
    cleanDaysState(true)
  }

  /* Cancel Reservations - congrats */
  const [congratsDialog, setCongratsDialog] = useState(false)
  const closeCongratsDialog = () => {
    setCongratsDialog(false)
    refreshCalendar()
    setVisibilityMultipleSideBar(false)
  }
  const congratsDialogUI = () => {
    return (
      <Dialog
        modal
        header=""
        visible={congratsDialog}
        position={'center'}
        style={{ width: '400px' }}
        footer={renderCongratsDialogFooter()}
        onHide={closeCongratsDialog}
        draggable={false}
        resizable={false}>
        <div className="d-flex flex-column of-newplan-confirm-dialog">
          <div className="of-confirm-image">
            <img src={successDialogImage} alt="" />
          </div>
          <div className="">
            <p className="of-confirm-info mt-2">{t('success-text.success')}</p>
          </div>
          <div className="of-confirm-detail">
            <p>{t('deletion-texts.deletion-res')}</p>
          </div>
        </div>
      </Dialog>
    )
  }
  const renderCongratsDialogFooter = () => {
    return (
      <div className="d-flex flex-row of-confirm-footer">
        <Button
          label={t('buttons.back-to-calendar')}
          onClick={closeCongratsDialog}
          className="w-100 of-button-primary"
        />
      </div>
    )
  }

  /* ################## Doctor Appointment ################## */
  const [docModal, setDocModal] = useState(false)
  const [appointmentNotFound, setAppointmentNotFound] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [selectedDay, setSelectedDay] = useState<ISideBarModel | undefined>()
  const getSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openDoctorModal() {
    const date = getSelectedDay()
    const selectedDayDate = moment(date?.day.date).toISOString()
    HealthService.getLocationsByReferanceDate(selectedDayDate).then((r) => {
      if (!r.data.length) {
        setAppointmentNotFound(true)
        setVisibilityMultipleSideBar(false)
      } else {
        setDocModal(true)
        setShowDocModal(true)
        setVisibilityMultipleSideBar(false)
      }
    })
  }

  function closeDoctorModal() {
    setVisibilityMultipleSideBar(true)
    setDocModal(false)
    setShowDocModal(false)
  }
  /* ################## Doctor Appointment ################## */

  /* TODO: handle generically other campus reservation modals etc. */
  /* ################## Coiffeur Reservation ################## */
  const [coiffeurModal, setCoiffeurModal] = useState(false)
  const [showCoiffeurModal, setShowCoiffeurModal] = useState(false)
  const [selectedCoiffeurDay, setSelectedCoiffeurDay] = useState<ISideBarModel | undefined>()
  const getCoiffeurSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedCoiffeurDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openCoiffeurModal() {
    getCoiffeurSelectedDay()
    setCoiffeurModal(true)
    setShowCoiffeurModal(true)
    setVisibilityMultipleSideBar(false)
  }

  function closeCoiffeurModal() {
    setVisibilityMultipleSideBar(true)
    setCoiffeurModal(false)
    setShowCoiffeurModal(false)
  }
  /* ################## Coiffeur Reservation ################## */

  /* ################## GYM Reservation ################## */
  const [gymModal, setGymModal] = useState(false)
  const [showGymModal, setShowGymModal] = useState(false)
  const [selectedGymDay, setSelectedGymDay] = useState<ISideBarModel | undefined>()
  const getGymSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedGymDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openGymModal() {
    getGymSelectedDay()
    setGymModal(true)
    setShowGymModal(true)
    setVisibilityMultipleSideBar(false)
  }

  function closeGymModal() {
    setVisibilityMultipleSideBar(true)
    setGymModal(false)
    setShowGymModal(false)
  }
  /* ################## GYM Reservation ################## */

  /* ################## Lactation Reservation ################## */
  const [lactationModal, setLactationModal] = useState(false)
  const [showLactationModal, setShowLactationModal] = useState(false)
  const [selectedLactationDay, setSelectedLactationDay] = useState<ISideBarModel | undefined>()
  const getLactationSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedLactationDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openLactationModal() {
    getLactationSelectedDay()
    setLactationModal(true)
    setShowLactationModal(true)
    setVisibilityMultipleSideBar(false)
  }

  function closeLactationModal() {
    setVisibilityMultipleSideBar(true)
    setLactationModal(false)
    setShowLactationModal(false)
  }
  /* ################## Lactation Reservation ################## */

  /* ################## Meeting Reservation ################## */
  const [meetingModal, setMeetingModal] = useState(false)
  const [showMeetingModal, setShowMeetingModal] = useState(false)
  const [selectedMeetingDay, setSelectedMeetingDay] = useState<ISideBarModel | undefined>()
  const getMeetingSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedMeetingDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openMeetingModal() {
    getMeetingSelectedDay()
    setMeetingModal(true)
    setShowMeetingModal(true)
    setVisibilityMultipleSideBar(false)
  }

  function closeMeetingModal() {
    setVisibilityMultipleSideBar(true)
    setMeetingModal(false)
    setShowMeetingModal(false)
  }
  /* ################## Meeting Reservation ################## */

  /* ################## Pool Vehicle Reservation ################## */
  const [poolVehicle, setPoolVehicleModal] = useState(false)
  const [showPoolVehicleModal, setShowPoolVehicleModal] = useState(false)
  const [selectedVehicleDay, setSelectedVehicleDay] = useState<ISideBarModel | undefined>()
  const getPoolVehicleSelectedDay = () => {
    const dayList = sideBarModelList.filter((x) => x.day.isChecked)
    const onCampusDisabled = dayList.length > 1
    if (!onCampusDisabled) {
      setSelectedVehicleDay(dayList[0])
      return dayList[0]
    } else return
  }

  function openPoolVehicleModal() {
    getPoolVehicleSelectedDay()
    setPoolVehicleModal(true)
    setShowPoolVehicleModal(true)
    setVisibilityMultipleSideBar(false)
  }

  function closePoolVehicleModal() {
    setVisibilityMultipleSideBar(true)
    setPoolVehicleModal(false)
    setShowPoolVehicleModal(false)
  }
  /* ################## Pool Vehicle Reservation ################## */

  return (
    <>
      <div className="row">
        <CalendarStatistic ref={statisticRef} employeeSummary={employeeSummary} />
      </div>
      <div className="row mt-3 mb-5">
        <div className="col-md-3 of-bordered-box">
          <CalendarPersonnelList
            ref={personnalListRef}
            employeeSummary={employeeSummary}
            data={calendarPersonnalModel}
            searchFirmEmployee={searchFirmEmployee!}
            onSearchFirmEmployee={onSearchFirmEmployee}
            onIsMyCalendar={onIsMyCalendar}
            showSquadsEvents={(arg0: any) => onSquadsEvent(arg0)}
            onClickPersonnel={(arg0: any) => onClickPersonnel(arg0)}
            onClickSquadsEvents={(arg0: any) => onClickSquadsEvents(arg0)}
          />
        </div>
        <div className="col-md-9 of-calendar-container ">
          <OfCalendar
            squadsEvents={squadsEvents}
            showSquadsEvent={showSquadsEvent}
            isMyCalendar={isMyCalendar}
            filterSquadsEvents={filterSquadsEvents}
            selectedEmployee={selectedPersonnal}
            events={calendarEvents}
            showLoader={showCalendarLoader}
            onChangeMonth={onChangeCalendarMonth}
            onSelectDay={handleCalendarSelectedDayClick}
            handleMultipleCalendarSelectedDay={handleMultipleCalendarSelectedDay}
            ofDay={ofDay}
            ofDayListCount={ofDayListCount}
          />
        </div>
      </div>
      <Sidebar
        blockScroll
        style={{ width: '500px' }}
        className="of-sidebar of-bordered-sidebar-box"
        visible={visibleRight}
        position="right"
        onHide={() => setVisibleRight(false)}>
        {getPlanUI()}
      </Sidebar>
      <Sidebar
        blockScroll
        id="multiple-sidebar-1"
        style={{ width: '500px' }}
        className="of-sidebar of-bordered-sidebar-box"
        visible={visiblityMultipleSideBar}
        position="right"
        onHide={() => setVisibilityMultipleSideBar(false)}>
        {getMultiplePlanUI()}
      </Sidebar>
      {alertDialogUI()}
      {successAlertDialogDeleteHomePlan()}
      {successAlertDialogDeleteOfficePlan()}
      {errorNotification()}
      {appointmentNotFoundNotification()}

      {/* Doctor Appointment Modals */}
      {showDocModal && (
        <DoctorSelectModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedDay}
          modal={docModal}
          setModal={setDocModal}
          closeDoctorModal={closeDoctorModal}
          resetAll={setShowDocModal}
        />
      )}
      {/* Coiffeur Reservation Modals */}
      {showCoiffeurModal && (
        <CalendarCoiffeurSearchModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedCoiffeurDay}
          modal={coiffeurModal}
          setModal={setCoiffeurModal}
          closeCoiffeurModal={closeCoiffeurModal}
          resetAll={setShowCoiffeurModal}
        />
      )}
      {/* Gym Reservation Modals */}
      {!!showGymModal && (
        <CalendarGymSearchModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedGymDay}
          modal={gymModal}
          setModal={setGymModal}
          closeGymModal={closeGymModal}
          resetAll={setShowGymModal}
        />
      )}
      {/* Lactation Reservation Modals */}
      {showLactationModal && (
        <LactationRoomSearchModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedLactationDay}
          modal={lactationModal}
          setModal={setLactationModal}
          closeLactationModal={closeLactationModal}
          resetAll={setShowLactationModal}
        />
      )}
      {/* Meeting Reservation Modals */}
      {showMeetingModal && (
        <MeetingRoomSearchModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedMeetingDay}
          modal={meetingModal}
          setModal={setMeetingModal}
          closeMeetingModal={closeMeetingModal}
          resetAll={setShowMeetingModal}
        />
      )}

      {/* Pool Vehicle Reservation Modals */}
      {showPoolVehicleModal && (
        <PoolVehicleSearchModal
          sidebarAfterSave={refreshCalendar}
          selectedDay={selectedVehicleDay}
          modal={poolVehicle}
          setModal={setPoolVehicleModal}
          closeVehicleModal={closePoolVehicleModal}
          resetAll={setShowPoolVehicleModal}
        />
      )}

      {/* Cancel Reservation Congrats */}
      {congratsDialogUI()}
    </>
  )
}

export default CalendarHomePage
